import { ApolloError } from '@apollo/client';
import { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import {
  BillingModelType,
  ContractTemplateType,
  ContractType,
  CostShareSetupOptionType,
  EngagementType,
  PackageRulesType,
  PackageType,
  useGetAllEngagementTypesQuery,
  useGetAllPackagesQuery,
  useGetBillingModelsQuery,
  useGetContractTemplatesQuery,
  useGetCostShareSetupOptionsQuery,
  useGetPackageRulesQuery,
} from '../../../types';
import { selectSorterFunction } from '../../../utils/bills-utils';
import { computeStartDateDefault } from '../../../utils/contract-utils';

interface Data {
  defaultStartDateCalenderDate: Dayjs;
  engagementTypes: EngagementType[];
  availablePackages: PackageType[];
  billingModels: BillingModelType[];
  templates: ContractTemplateType[];
  packageRules: PackageRulesType;
  defaultInitialPackage: Partial<PackageType>;
  costShareSetupOptions: CostShareSetupOptionType;
}

interface UseGetDefaultReturn {
  data: Data | undefined;
  isLoading: boolean;
  error: ApolloError | undefined;
}

export function useGetDefaultPackageFormValues(
  currentContracts: ContractType[],
): UseGetDefaultReturn {
  const defaultStartDateCalenderDate = useMemo(
    () => computeStartDateDefault(currentContracts),
    [currentContracts],
  );
  const defaultInitialPackage = useMemo(
    () => ({ id: 0, name: '', packageOfferings: [] }),
    [],
  );

  const {
    data: engagementTypes,
    loading: engagementTypesLoading,
    error: engagementTypesError,
  } = useGetAllEngagementTypesQuery();
  const {
    data: availablePackages,
    loading: packagesLoading,
    error: packagesError,
  } = useGetAllPackagesQuery();
  const {
    data: billingModels,
    loading: billingModelsLoading,
    error: billingModelsError,
  } = useGetBillingModelsQuery();
  const {
    data: templates,
    loading: templatesLoading,
    error: templatesError,
  } = useGetContractTemplatesQuery();
  const {
    data: packageRules,
    loading: packageRulesLoading,
    error: packageRulesError,
  } = useGetPackageRulesQuery();
  const {
    data: costShareSetupOptions,
    loading: costShareSetupOptionsLoading,
    error: costShareSetupOptionsError,
  } = useGetCostShareSetupOptionsQuery();

  const combinedError =
    engagementTypesError ||
    packagesError ||
    billingModelsError ||
    templatesError ||
    packageRulesError ||
    costShareSetupOptionsError;

  const isLoading =
    engagementTypesLoading ||
    packagesLoading ||
    billingModelsLoading ||
    templatesLoading ||
    packageRulesLoading ||
    costShareSetupOptionsLoading;

  const processedData = useMemo(() => {
    if (
      isLoading ||
      combinedError ||
      !engagementTypes?.getAllEngagementTypes ||
      !availablePackages?.getAllPackages ||
      !billingModels?.getBillingModels ||
      !templates?.getContractTemplates ||
      !packageRules?.getPackageRules ||
      !costShareSetupOptions?.getCostShareSetupOptions
    ) {
      return undefined;
    }

    return {
      defaultStartDateCalenderDate,
      engagementTypes: engagementTypes.getAllEngagementTypes,
      availablePackages: availablePackages.getAllPackages,
      billingModels: [...billingModels.getBillingModels].sort((a, b) =>
        selectSorterFunction(a.name, b.name),
      ),
      templates: templates.getContractTemplates,
      packageRules: packageRules.getPackageRules,
      defaultInitialPackage,
      costShareSetupOptions: costShareSetupOptions.getCostShareSetupOptions,
    };
  }, [
    engagementTypes?.getAllEngagementTypes,
    availablePackages?.getAllPackages,
    billingModels?.getBillingModels,
    templates?.getContractTemplates,
    packageRules?.getPackageRules,
    isLoading,
    combinedError,
    defaultStartDateCalenderDate,
    defaultInitialPackage,
    costShareSetupOptions?.getCostShareSetupOptions,
  ]);

  return {
    data: processedData,
    isLoading,
    error: combinedError,
  };
}
